// components/StartDivorceForm.tsx
import React, { useState } from "react";
import { Button } from "../../../../../design/button";
import { Field, Label } from "../../../../../design/fieldset";
import OnboardingContainer from "../../../components/OnboardingContainer";
import { Checkbox, CheckboxField } from "../../../../../design/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../design/table";
import moment from "moment";
import { Input } from "../../../../../design/input";
import { Text } from "../../../../../design/text";
import { TrashIcon } from "@heroicons/react/16/solid";
import { Intake } from "../../../../../models/Intake";

const OnboardingKidsStep: React.FC<{
  formData: Omit<
    Intake,
    "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
  >;
  setFormData: React.Dispatch<
    React.SetStateAction<
      Omit<
        Intake,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
      >
    >
  >;
  onBack: () => void;
  onStepComplete: () => void;
}> = ({ formData, setFormData, onBack, onStepComplete }) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onStepComplete(); // Call step complete when ready
  };

  const addParticipant = () => {
    setFormData({
      ...formData,
      kids: [...(formData.kids || []), { name: "", dateOfBirth: null }],
    });
  };

  const updateParticipant = (
    index: number,
    field: "name" | "dateOfBirth",
    value: string,
  ) => {
    const updatedKids = [...(formData.kids || [])];
    if (field === "name") {
      updatedKids[index].name = value;
    } else if (field === "dateOfBirth") {
      updatedKids[index].dateOfBirth = value ? new Date(value) : null;
    }
    setFormData({ ...formData, kids: updatedKids });
  };

  const isSubmitEnabled = () => {
    if (!formData.hasKids) return true;
    return formData.kids?.every((kid) => kid.name && kid.dateOfBirth);
  };

  return (
    <OnboardingContainer title={"Kids"}>
      <>
        <p className="mb-6 text-gray-700">
          Do you and your spouse have any children under 18?
        </p>
        <form onSubmit={handleSubmit} className="space-y-6">
          <CheckboxField>
            <Label>Yes, we have children under 18</Label>
            <Checkbox
              checked={formData.hasKids}
              onChange={(checked) => {
                if (!checked) {
                  setFormData({ ...formData, hasKids: false, kids: [] });
                } else {
                  setFormData({
                    ...formData,
                    hasKids: true,
                    kids: [{ name: "", dateOfBirth: null }],
                  });
                }
              }}
              className="mt-2"
            />
          </CheckboxField>

          {formData.hasKids && (
            <>
              <Text>
                Please provide the names and dates of birth of your children.
              </Text>
              {formData.kids && formData.kids.length > 0 && (
                <Table className="mt-4 [--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                  <TableHead>
                    <TableRow>
                      <TableHeader>Name</TableHeader>
                      <TableHeader>Date Of Birth</TableHeader>
                      <TableHeader />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.kids.map((kid, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Input
                            type="text"
                            placeholder="Child's Name"
                            value={kid.name}
                            className="w-full"
                            onChange={(e) =>
                              updateParticipant(index, "name", e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="date"
                            placeholder="Child's Date of Birth"
                            value={
                              kid.dateOfBirth
                                ? kid.dateOfBirth.toISOString().split("T")[0]
                                : ""
                            }
                            className="w-full"
                            onChange={(e) =>
                              updateParticipant(
                                index,
                                "dateOfBirth",
                                e.target.value,
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            color="red"
                            onClick={() => {
                              const updatedKids = [...(formData.kids || [])];
                              updatedKids.splice(index, 1);
                              setFormData({ ...formData, kids: updatedKids });
                            }}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Button
                type="button"
                className="w-full mt-4"
                onClick={addParticipant}
              >
                Add Another Child
              </Button>
            </>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={!isSubmitEnabled()}
          >
            Continue
          </Button>
        </form>
      </>
    </OnboardingContainer>
  );
};

export default OnboardingKidsStep;
